// index.tsx
import React, { useCallback } from 'react'
import { Spin } from 'antd'
import { format } from 'date-fns'
import { COLORS } from 'tokens'

import {
  CalendarContainer,
  FixedHeaderSection,
  CountHeaderContainer,
  CountHeaderText,
  HeaderRow,
  HeaderCell,
  MainContentContainer,
  TimeColumnContainer,
  TimeCell,
  Time,
  LoadingContainer,
  CarouselContainer,
  StyledCarousel,
  ScrollableGridContainer,
  LiveTimeIndicator,
  CalendarGrid,
  CalendarCell,
  QuarterRow,
  BlockedSlotOverlay,
} from './index.styled'

import { useSchedule } from './useSchedule'
/* --------------------------------------------------
   EVENT BAR (UI ONLY)
-------------------------------------------------- */
const EventBar = ({
  count,
  color,
  outlined,
  opacity,
}: {
  count: number
  color: string
  outlined: boolean
  opacity: number
}) => {
  let barWidth = '100%'
  if (count === 2) barWidth = '50%'
  if (count === 3) barWidth = '33.33%'

  return (
    <div style={{ display: 'flex', gap: '2px', width: '100%' }}>
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          style={{
            width: barWidth,
            height: '11.5px',
            background: color,
            // outline: outlined ? '1px dashed' : 'none',
            border: '1px solid #000000',
            opacity,
            borderRadius: '2px',
          }}
        />
      ))}
    </div>
  )
}

/* --------------------------------------------------
   CALENDAR WEEK (UI ONLY)
-------------------------------------------------- */
/* Merge contiguous blocked quarter-hours into single overlay blocks */
const getBlockedRuns = (quarters: boolean[]) => {
  const runs: Array<{ start: number; length: number }> = []
  let runStart = -1

  quarters.forEach((blocked, q) => {
    if (blocked && runStart === -1) runStart = q
    if (runStart !== -1 && (!blocked || q === quarters.length - 1)) {
      const runEnd = blocked ? q + 1 : q
      runs.push({ start: runStart, length: runEnd - runStart })
      runStart = -1
    }
  })

  return runs
}

const CalendarWeek = ({
  weekStart,
  calendarData,
  blockedData,
  timeSlots,
}: {
  weekStart: Date
  calendarData: any
  blockedData: any
  timeSlots: any
}) => {
  const generateDays = useCallback(() => {
    const arr = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart)
      d.setDate(d.getDate() + i)
      arr.push({
        date: d,
        dayName: format(d, 'EEE').toUpperCase(),
      })
    }
    return arr
  }, [weekStart])

  const days = generateDays()

  const getEventsForQuarter = (dayLabel: string, hour: number, quarter: number) => {
    return calendarData?.[dayLabel]?.[hour]?.[quarter] || []
  }

  const getBlockedQuartersForHour = (dayLabel: string, hour: number): boolean[] => {
    return blockedData?.[dayLabel]?.[hour] || []
  }

  return (
    <CalendarGrid>
      {timeSlots.map((slot: any, i: number) => (
        <React.Fragment key={i}>
          {days.map((day, di) => {
            // Quarter height is 25% of the cell's 63px padding box (64px minus
            // the 1px bottom border); the borderless last row is a full 64px.
            const quarterHeight = i === timeSlots.length - 1 ? 16 : 15.75

            return (
              <CalendarCell key={di}>
                {getBlockedRuns(getBlockedQuartersForHour(day.dayName, slot.value)).map((run, ri) => (
                  <BlockedSlotOverlay
                    key={ri}
                    style={{
                      top: `${run.start * 25}%`,
                      height: `${run.length * 25}%`,
                      backgroundPosition: `0 ${-(i * 64 + run.start * quarterHeight)}px`,
                    }}
                  />
                ))}
                {[0, 1, 2, 3].map((q) => {
                  const events = getEventsForQuarter(day.dayName, slot.value, q)

                  return (
                    <QuarterRow key={q}>
                      {events.map((ev: any, idx: number) => (
                        <EventBar
                          key={idx}
                          count={ev.count}
                          color={ev.color}
                          opacity={ev.opacity}
                          outlined={ev.outlined}
                        />
                      ))}
                    </QuarterRow>
                  )
                })}
              </CalendarCell>
            )
          })}
        </React.Fragment>
      ))}
    </CalendarGrid>
  )
}

/* --------------------------------------------------
   MAIN COMPONENT
-------------------------------------------------- */
const ScheduleScreen = () => {
  const {
    loading,
    weekStarts,
    headerDays,
    calendarData,
    blockedData,
    currentTime,
    carouselRef,
    getCurrentTimeOffset,
    handleCarouselChange,
    dayCounts,
    totalWeekAppointments,
    centralWeekIndex,
  } = useSchedule()

  const timeSlots = [
    { label: '8 AM', value: 8 },
    { label: '9 AM', value: 9 },
    { label: '10 AM', value: 10 },
    { label: '11 AM', value: 11 },
    { label: '12 PM', value: 12 },
    { label: '1 PM', value: 13 },
    { label: '2 PM', value: 14 },
    { label: '3 PM', value: 15 },
    { label: '4 PM', value: 16 },
    { label: '5 PM', value: 17 },
    { label: '6 PM', value: 18 },
    { label: '7 PM', value: 19 },
    { label: '8 PM', value: 20 },
  ]

  // const getOverlappedTimeLabel = () => {
  //   const offset = getCurrentTimeOffset()
  //   if (offset === null) return null

  //   // Find which row the line is inside
  //   const rowIndex = Math.floor(offset / 64)

  //   // Use timeSlots[rowIndex] to identify label
  //   return timeSlots[rowIndex]?.label || null
  // }

  // const overlappedLabel = getOverlappedTimeLabel()

  return (
    <CalendarContainer>
      <FixedHeaderSection>
        <CountHeaderContainer>
          <CountHeaderText>{totalWeekAppointments}</CountHeaderText>
        </CountHeaderContainer>

        <HeaderRow>
          {headerDays.map((h, i) => {
            const dayKey = format(h.date, 'EEE').toUpperCase()
            // total | non-member (conversion opportunities) | converted (credited to first-visit day)
            const counts = dayCounts[dayKey] || {
              totalCount: 0,
              nonMemberCount: 0,
              convertedCount: 0,
            }
            return (
              <HeaderCell key={i}>
                <div style={{ fontWeight: 700 }}>{h.dayName}</div>
                <div>{`${counts.totalCount} | ${counts.nonMemberCount} | ${counts.convertedCount}`}</div>
              </HeaderCell>
            )
          })}
        </HeaderRow>
      </FixedHeaderSection>

      <MainContentContainer>
        <TimeColumnContainer>
          {/* Live time indicator */}
          {(() => {
            const offset = getCurrentTimeOffset()
            if (offset === null) return null
            return (
              <LiveTimeIndicator top={offset} style={{ top: `calc(${offset}px + 64px)` }}>
                <span style={{ color: COLORS.textPrimary }}>{format(currentTime, 'h:mm a')}</span>
              </LiveTimeIndicator>
            )
          })()}

          {timeSlots.map((slot, i) => (
            <TimeCell key={i}>
              <Time>{slot.label}</Time>
            </TimeCell>
          ))}
          {/* {timeSlots.map((slot, i) => (
            <TimeCell key={i}>
              <Time style={{ visibility: overlappedLabel === slot.label ? 'hidden' : 'visible' }}>{slot.label}</Time>
            </TimeCell>
          ))} */}
        </TimeColumnContainer>

        {loading ? (
          <LoadingContainer>
            <Spin size="large" />
          </LoadingContainer>
        ) : (
          <CarouselContainer>
            <StyledCarousel
              ref={carouselRef}
              dots={false}
              draggable
              infinite={false}
              speed={500}
              swipeToSlide
              beforeChange={(_, next) => handleCarouselChange(next)}
              initialSlide={centralWeekIndex}
              swipe
            >
              {weekStarts.map((weekStart, wi) => {
                // const offset = getCurrentTimeOffset()

                return (
                  <div key={wi}>
                    <ScrollableGridContainer>
                      <CalendarWeek
                        weekStart={weekStart}
                        timeSlots={timeSlots}
                        calendarData={calendarData[format(weekStart, 'yyyy-MM-dd')] || {}}
                        blockedData={blockedData[format(weekStart, 'yyyy-MM-dd')] || {}}
                      />
                    </ScrollableGridContainer>
                  </div>
                )
              })}
            </StyledCarousel>
          </CarouselContainer>
        )}
      </MainContentContainer>
    </CalendarContainer>
  )
}

export default ScheduleScreen
